// Physical book-page pagination for Book Preview.
// Mirrors the export PDF's trim/typography assumptions (ReportLab Times 11/leading 17,
// 0.5in margins, trim-sized pages) so the on-screen preview is representative of the print.
import { imageUrl } from "@/lib/api";

// --- lightweight inline markdown -> HTML (bold/italic/code), HTML-escaped ---
export const inlineHtml = (s) =>
  (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/_{3,}/g, '<span class="blank"></span>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>");

// Parse a chapter's markdown into block descriptors (same model as the exporters: backend/md_blocks.py).
const SEP = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/;
const ROW = /^\|.*\|$/;
const LIST = /^([-*+]\s+\[( |x|X)\]|[-*+]|\d+[.)])\s+(.*)/;
const cells = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
export function parseBlocks(md) {
  const lines = (md || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] || "").trim();
    if (!line) { i++; continue; }
    if (ROW.test(line) && SEP.test((lines[i + 1] || "").trim())) {
      const head = cells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && ROW.test(lines[i].trim())) { rows.push(cells(lines[i]).map(inlineHtml)); i++; }
      blocks.push({ kind: "table", head: head.map(inlineHtml), rows });
      continue;
    }
    if (/^_{8,}$/.test(line)) {
      let n = 0;
      while (i < lines.length && (/^_{8,}$/.test(lines[i].trim()) || !lines[i].trim())) { if (lines[i].trim()) n++; i++; }
      blocks.push({ kind: "lines", n: Math.max(1, n) });
      continue;
    }
    if (/^((\*\s*){3,}|-{3,}|#|~{3,})$/.test(line)) { blocks.push({ kind: "break" }); i++; continue; }
    const hm = line.match(/^(#{1,6})\s+(.*)/);
    if (hm) { blocks.push({ kind: hm[1].length <= 2 ? "h2" : "h3", html: inlineHtml(hm[2]) }); i++; continue; }
    if (/^>/.test(line)) {
      const q = [];
      while (i < lines.length && /^>/.test(lines[i].trim())) { q.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      blocks.push({ kind: "quote", html: inlineHtml(q.join(" ")) });
      continue;
    }
    const lm = line.match(LIST);
    if (lm) {
      const kindOf = (m) => (m[1].includes("[") ? "check" : /^\d/.test(m[1]) ? "ol" : "ul");
      const kind = kindOf(lm);
      const items = [];
      const checked = [];
      while (i < lines.length) {
        const mm = (lines[i] || "").trim().match(LIST);
        if (!mm || kindOf(mm) !== kind) break;
        items.push(inlineHtml(mm[3]));
        checked.push(/x/i.test(mm[2] || ""));
        i++;
      }
      blocks.push({ kind, items, checked });
      continue;
    }
    blocks.push({ kind: "p", text: line, html: inlineHtml(line) });
    i++;
  }
  return blocks;
}

// Compute physical page geometry + scaled typography (px) from a trim and a display width.
export function computeDims(trim, pageW) {
  const w = trim?.w || 6;
  const h = trim?.h || 9;
  const scale = pageW / w;               // px per inch
  const pageH = Math.round(pageW * (h / w));
  const margin = Math.round(scale * 0.5);
  const footer = Math.round(scale * 0.3);
  const textW = pageW - 2 * margin;
  const textH = pageH - 2 * margin - footer;
  return {
    pageW: Math.round(pageW), pageH, margin, footer, textW, textH, scale,
    bodyPx: scale * 11 / 72,
    linePx: scale * 17 / 72,
    imgH: Math.round(textW * 0.52),
  };
}

function blockHtml(b, dims) {
  switch (b.kind) {
    case "chapterhead":
      return `<div class="chead"><div class="cnum">Chapter ${b.number}</div>` +
        `<div class="ctitle">${inlineHtml(b.title || "")}</div><div class="crule"></div></div>`;
    case "image":
      return `<figure class="cfig" style="height:${dims.imgH}px"><img src="${imageUrl(b.id)}?t=${b.id}" alt=""/>` +
        (b.caption ? `<figcaption>${inlineHtml(b.caption)}</figcaption>` : "") + `</figure>`;
    case "h2": return `<h2>${b.html}</h2>`;
    case "h3": return `<h3>${b.html}</h3>`;
    case "ul": return `<ul>${b.items.map((i) => `<li>${i}</li>`).join("")}</ul>`;
    case "ol": return `<ol${b.start > 1 ? ` start="${b.start}"` : ""}>${b.items.map((i) => `<li>${i}</li>`).join("")}</ol>`;
    case "check": return `<ul class="check">${b.items.map((i, k) => `<li${(b.checked || [])[k] ? ' class="done"' : ""}>${i}</li>`).join("")}</ul>`;
    case "table":
      return `<table class="mdt"><thead><tr>${b.head.map((c) => `<th>${c}</th>`).join("")}</tr></thead>` +
        `<tbody>${b.rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    case "break": return `<p class="sbreak">*&#8195;*&#8195;*</p>`;
    case "lines": return `<div class="wlines">${'<div class="wl"></div>'.repeat(b.n)}</div>`;
    case "quote": return `<blockquote>${b.html}</blockquote>`;
    case "p": default: return `<p${b.cont ? ' class="cont"' : ""}>${b.html}</p>`;
  }
}

// Close/reopen inline markdown so a paragraph split across pages never shows raw ** or *.
const balance = (first, rest) => {
  let a = first, b = rest;
  for (const mk of ["**", "__", "`"]) {
    if ((a.split(mk).length - 1) % 2) { a += mk; b = mk + b; }
  }
  const lone = (t) => (t.replace(/\*\*/g, "").match(/\*/g) || []).length;
  if (lone(a) % 2) { a += "*"; b = "*" + b; }
  return [a, b];
};
const LISTS = ["ul", "ol", "check"];
const isLabel = (b) => b && b.kind === "p" && /^\*\*[^*]+\*\*:?$/.test((b.text || "").trim());
// Smallest piece of a block that must travel with a heading/label placed just above it.
const leadOf = (work, j, dims) => {
  const b = work[j];
  if (!b) return "";
  if (b.kind === "h2" || b.kind === "h3" || isLabel(b)) return blockHtml(b, dims) + leadOf(work, j + 1, dims);  // heading chains
  if (b.kind === "p") return "<p>x<br/>x</p>";
  if (LISTS.includes(b.kind)) return blockHtml({ ...b, items: b.items.slice(0, 1) }, dims);
  if (b.kind === "table") return blockHtml({ ...b, rows: b.rows.slice(0, 1) }, dims);
  return blockHtml(b, dims);
};

// Split one chapter's blocks into an array of physical-page HTML strings.
export function paginateChapter(blocks, measurer, dims) {
  const textH = dims.textH;
  const pages = [];
  let cur = "";
  const fits = (html) => { measurer.innerHTML = html; return measurer.scrollHeight <= textH; };
  const push = () => { if (cur.trim()) { pages.push(cur); cur = ""; } };

  const work = blocks.slice();
  let guard = 0;
  for (let i = 0; i < work.length; i++) {
    if (++guard > 20000) break;
    const b = work[i];
    const bh = blockHtml(b, dims);
    const isHead = b.kind === "h2" || b.kind === "h3" || isLabel(b);
    // keep a heading (or a bold label line) with the start of whatever follows it
    if (fits(cur + bh) && !(isHead && cur.trim() && !fits(cur + bh + leadOf(work, i + 1, dims)))) { cur += bh; continue; }
    if (isHead && cur.trim()) { push(); i--; continue; }

    if (LISTS.includes(b.kind) && b.items.length > 1) {
      let lo = 1, hi = b.items.length - 1, best = 0;
      const part = (n) => ({ ...b, items: b.items.slice(0, n), checked: (b.checked || []).slice(0, n) });
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (fits(cur + blockHtml(part(mid), dims))) { best = mid; lo = mid + 1; } else hi = mid - 1;
      }
      if (best === 0 && cur.trim()) { push(); i--; continue; }
      best = Math.max(best, 1);
      cur += blockHtml(part(best), dims);
      push();
      work.splice(i + 1, 0, { ...b, items: b.items.slice(best), checked: (b.checked || []).slice(best),
        start: (b.start || 1) + best });
      continue;
    }

    if (b.kind === "table" && b.rows.length > 1) {
      let lo = 1, hi = b.rows.length - 1, best = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (fits(cur + blockHtml({ ...b, rows: b.rows.slice(0, mid) }, dims))) { best = mid; lo = mid + 1; } else hi = mid - 1;
      }
      if (best === 0 && cur.trim()) { push(); i--; continue; }
      best = Math.max(best, 1);
      cur += blockHtml({ ...b, rows: b.rows.slice(0, best) }, dims);
      push();
      work.splice(i + 1, 0, { ...b, rows: b.rows.slice(best) });  // header repeats on the next page
      continue;
    }

    if (b.kind === "p") {
      const words = (b.text || "").split(/\s+/).filter(Boolean);
      // largest word-count that still fits on the current page
      let lo = 1, hi = words.length, best = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const html = cur + `<p${b.cont ? ' class="cont"' : ""}>${inlineHtml(words.slice(0, mid).join(" "))}</p>`;
        if (fits(html)) { best = mid; lo = mid + 1; } else hi = mid - 1;
      }
      if (best === 0) {
        if (cur.trim()) { push(); i--; continue; }   // retry whole paragraph on a fresh page
        best = 1;                                     // never stall on an empty page
      }
      const [headTxt, remTxt] = balance(words.slice(0, best).join(" "), words.slice(best).join(" "));
      cur += `<p${b.cont ? ' class="cont"' : ""}>${inlineHtml(headTxt)}</p>`;
      push();
      if (words.slice(best).length) work.splice(i + 1, 0, { kind: "p", text: remTxt, html: inlineHtml(remTxt), cont: true });
    } else {
      if (cur.trim()) { push(); i--; }               // move whole block to a fresh page
      else { cur += bh; push(); }                    // block alone is taller than a page
    }
  }
  push();
  return pages.length ? pages : [""];
}

// Build the full ordered list of spreads for a book.
// children/puzzle = one physical page per chapter; prose = measured flow.
export function buildSpreads(book, measurer, dims, { hasToc, isPuzzle, stripDupHeading }) {
  const spreads = [{ type: "cover" }];
  const chapterStartPage = {};        // chapterIndex -> manuscript page number
  let manu = 0;
  const pushChildrenOrPuzzle = (ch, ci, mode) => {
    manu += 1;
    chapterStartPage[ci] = manu;
    spreads.push({ type: "page", mode, chapterIndex: ci, chapterNumber: ch.number,
      chapterTitle: ch.title, isChapterStart: true, image_id: ch.image_id,
      content: ch.content, manuscriptPage: manu });
  };

  (book.chapters || []).forEach((ch, ci) => {
    if (isPuzzle) return pushChildrenOrPuzzle(ch, ci, "puzzle");
    if (book.mode === "children") return pushChildrenOrPuzzle(ch, ci, "children");
    const blocks = [{ kind: "chapterhead", number: ch.number, title: ch.title }];
    if (ch.image_id) blocks.push({ kind: "image", id: ch.image_id, caption: ch.caption });
    parseBlocks(stripDupHeading(ch.content, ch.title)).forEach((b) => blocks.push(b));
    const htmlPages = paginateChapter(blocks, measurer, dims);
    htmlPages.forEach((html, pi) => {
      manu += 1;
      if (pi === 0) chapterStartPage[ci] = manu;
      spreads.push({ type: "page", mode: "prose", chapterIndex: ci, chapterNumber: ch.number,
        chapterTitle: ch.title, isChapterStart: pi === 0, html, manuscriptPage: manu });
    });
  });

  // TOC inserted after the cover, now that we know each chapter's start page.
  if (hasToc) {
    spreads.splice(1, 0, {
      type: "toc",
      entries: (book.chapters || []).map((ch, ci) => ({
        number: ch.number, title: ch.title, page: chapterStartPage[ci] || 1,
      })),
    });
  }
  return { list: spreads, totalManu: manu, chapterStartPage };
}
